// Foundations
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';

// RXJS
import { BehaviorSubject, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  mergeMap,
  takeUntil,
} from 'rxjs/operators';

// Models
import { MedicationType } from '@app/models/pet/medication-type.model';

// Services
import { PetService } from '@app/services/pet.service';

import * as lodash from 'lodash';

@Component({
  selector: 'app-medication-type-search',
  templateUrl: './medication-type-search.component.html',
})
export class MedicationTypeSearchComponent implements OnInit, OnDestroy {
  static SEARCH_DEBOUNCE_TIME = 300; // ms;

  // #region -> Property <-
  // ----------

  medicationTypeList$ = new BehaviorSubject<Array<MedicationType>>([]);
  selectedMedicationType$ = new BehaviorSubject<MedicationType>(null);

  // ----------
  // #endregion

  // ///////////////////////////

  // #region -> Property - UI <-
  // ----------
  @Input() index: number;
  @Input() selectedMed: MedicationType;
  @Input() selectedMedicationTypeIds: number[];
  @Output() selectFromDropdown = new EventEmitter();
  private _textChange$ = new Subject<string>();
  private filterText = '';

  // Unsubscribe all subscriptions from Observables in component
  private _unsubscribe$ = new Subject<void>();

  // ----------
  // #endregion

  // ///////////////////////////

  constructor(private petService: PetService) {
    this.selectedMedicationTypeIds = lodash.cloneDeep(
      this.selectedMedicationTypeIds
    );
  }

  // #region -> Lifecycle <-
  // ----------

  ngOnInit(): void {
    //
    this.filterText = this.selectedMed.name;
    this._listenToTextFieldChange();
  }

  ngOnDestroy() {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  // ----------
  // #endregion

  // ///////////////////////////

  // #region -> Method - UI <-
  // ---------
  closeDropdown(ret?: { id: number; name: string; index: number }) {
    this.filterText = ret.name;
    // update the id so if we blur dropdown we keep the proper value
    this.selectedMed.id = ret.id;
    this.selectFromDropdown.emit(ret);
  }

  // checks if we have changed text from selectedMed emit null
  blurDropdown() {
    if (this.selectedMed.name !== this.filterText) {
      this.selectFromDropdown.emit({
        id: null,
        name: this.filterText,
        index: this.index,
      });
    }
  }
  onTextChange(args): void {
    // Passing the value
    this.filterText = args.currentTarget.value;
    this._textChange$.next(this.filterText);
  }

  selectMedicationType(medicationType: MedicationType): void {
    this.selectedMed = medicationType;
    // Return selection
    this.closeDropdown({
      id: medicationType.id,
      name: medicationType.name,
      index: this.index,
    });
  }
  openSelection() {
    this._textChange$.next(this.filterText);
  }

  // ----------
  // #endregion

  // ///////////////////////////

  // #region -> Private Method <-
  // ----------

  private _listenToTextFieldChange() {
    //
    this._textChange$
      .pipe(
        debounceTime(MedicationTypeSearchComponent.SEARCH_DEBOUNCE_TIME),
        distinctUntilChanged(),
        // filter((searchText) => searchText.length !== 0),
        mergeMap((searchText) =>
          this.petService.getMedicationTypes(searchText)
        ),
        map((medicationTypeList) => {
          /*
          Filter out the "selected" types.
         */
          const filteredList = [];
          if (Array.isArray(medicationTypeList)) {
            medicationTypeList.forEach((type: MedicationType) => {
              if (!this.selectedMedicationTypeIds.includes(type.id)) {
                filteredList.push(type);
              }
            });
          }

          // Update
          this.medicationTypeList$.next(filteredList);
        }),
        takeUntil(this._unsubscribe$)
      )
      .subscribe();
  }

  getMedTypeLabel(med: MedicationType): string {
    if (med) {
      return med.name;
    }
  }

  // ----------
  // #endregion

  // ///////////////////////////
}
